// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './styles/global.css';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import CreatePost from './pages/CreatePost';
import ManagePosts from './pages/ManagePosts';
import CalendarPage from './pages/CalendarPage';
import ManageAccounts from './pages/ManageAccounts';
import ConnectAccount from './pages/ConnectAccount';
import ManageGroups from './pages/ManageGroups';
import CreateGroup from './pages/CreateGroup';
import Analytics from './pages/Analytics';
import DraftPosts from './pages/DraftPosts';
import PendingReview from './pages/PendingReview';
import BulkImport from './pages/BulkImport';
import LinkedInOAuthClose from './pages/LinkedInOAuthClose';
import Profile from './pages/Profile';
import Team from './pages/Team';
import AcceptInvite from './pages/AcceptInvite';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import AdminOverview from './pages/admin/AdminOverview';
import AdminUsers from './pages/admin/AdminUsers';
import AdminOrganizations from './pages/admin/AdminOrganizations';
import AdminActivity from './pages/admin/AdminActivity';
import NotFound, { NotFoundPanel } from './pages/NotFound';
import Home from './pages/site/Home';
import PrivacyPolicy from './pages/site/PrivacyPolicy';
import TermsOfService from './pages/site/TermsOfService';
import { useStore } from './store';

// The API enforces this too (backend routes/admin.js). Everyone else gets a plain
// 404, so the admin pages don't advertise themselves.
function SuperadminRoute({ children }) {
  const user = useStore(s => s.user);
  return user?.role === 'superadmin' ? children : <NotFoundPanel />;
}

// "/" is always the public home page (Google OAuth verification needs one); the app
// lives under /dashboard and the other app routes, which send signed-out users to /login.
function AppRoot() {
  const { user, authChecked } = useStore();
  if (!authChecked) return null;
  return user ? <Layout /> : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { user, authChecked } = useStore();
  if (!authChecked) return null;
  return !user ? children : <Navigate to="/dashboard" replace />;
}

export default function App() {
  const checkAuth = useStore(s => s.checkAuth);
  useEffect(() => { checkAuth(); }, [checkAuth]);

  return (
    <BrowserRouter>
      <ToastContainer position="bottom-right" autoClose={3000} />
      <Routes>
        <Route path="/oauth/linkedin" element={<LinkedInOAuthClose />} />
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        {/* Not PublicRoute: these links must work whether or not you're signed in */}
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/invite" element={<AcceptInvite />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/" element={<Home />} />
        <Route element={<AppRoot />}>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="posts/create" element={<CreatePost />} />
          <Route path="posts/edit/:postId" element={<CreatePost />} />
          <Route path="posts/manage" element={<ManagePosts />} />
          <Route path="posts/calendar" element={<CalendarPage />} />
          <Route path="accounts" element={<ManageAccounts />} />
          <Route path="accounts/connect" element={<ConnectAccount />} />
          <Route path="groups" element={<ManageGroups />} />
          <Route path="groups/create" element={<CreateGroup />} />
          <Route path="groups/edit/:id" element={<CreateGroup />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="posts/drafts" element={<DraftPosts />} />
          <Route path="posts/pending-review" element={<PendingReview />} />
          <Route path="posts/bulk-import" element={<BulkImport />} />
          <Route path="settings/profile" element={<Profile />} />
          <Route path="settings/team" element={<Team />} />
          <Route path="admin" element={<SuperadminRoute><AdminOverview /></SuperadminRoute>} />
          <Route path="admin/organizations" element={<SuperadminRoute><AdminOrganizations /></SuperadminRoute>} />
          <Route path="admin/users" element={<SuperadminRoute><AdminUsers /></SuperadminRoute>} />
          <Route path="admin/activity" element={<SuperadminRoute><AdminActivity /></SuperadminRoute>} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}
